import { firstValueFrom } from 'rxjs';

import type { ReservationValidations } from '@/data/models/reservation.model';
import reservationService from '@/services/reservationService';
import triplegService from '@/services/triplegService';
import tripLegUtils from '@/services/triplegUtils';

import { filterConfidenceErrorsForUnsetPayload } from '../utils/filterConfidenceErrorsForUnsetPayload';
import { filterAddressErrorsForOneSidedTripLegs } from '../utils/filterAddressErrorsForOneSidedTripLegs';
import {
  hasBlockingTripLegConfidenceError,
  mergeTripLegConfidenceResponsesIntoReservationPayload,
  resolveConfidenceErrorFieldPath,
  type TripLegConfidenceErrorPayload,
  type TripLegConfidenceStoredEntry,
  type TripLegScoreCacheEntry,
} from './tripLegConfidence';

const EMPTY_VALIDATIONS: ReservationValidations = {
  tripLegUpdates: [],
  errors: [],
  warnings: [],
  log: [],
};

export type BundledValidationInput = {
  reservation: unknown;
  addressValidationEnabled: boolean;
  isClientPortal: boolean;
};

export type BundledValidationOutput = {
  validations: ReservationValidations;
  confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry>;
  errorsByTripIndex: Record<number, TripLegConfidenceErrorPayload>;
};

/**
 * Address-keyed cache of in-flight / settled /score/trip-leg calls. Keyed by the
 * confidence payload (addresses only — the score is a pure function of them), so
 * concurrent or repeated runs for the same address share one network call. It's
 * module-scoped on purpose: a bundled run that gets cancelled mid-flight (e.g.
 * the estimated-pickup auto-fill firing a second FIELD_EDITED right after an
 * address change) would otherwise discard its result and the next run would
 * re-score. Cleared on form reset via `clearTripLegScoreCache`.
 */
const tripLegScoreCache = new Map<string, Promise<TripLegScoreCacheEntry>>();

export function clearTripLegScoreCache(): void {
  tripLegScoreCache.clear();
}

function scoreTripLeg(
  payload: ReturnType<typeof tripLegUtils.getTripLegConfidencePayload>,
  hasPickup: boolean,
  hasDropoff: boolean,
): Promise<TripLegScoreCacheEntry> {
  const key = JSON.stringify(payload);
  const cached = tripLegScoreCache.get(key);
  if (cached) {
    return cached;
  }
  const promise = firstValueFrom(triplegService.validateTripLegAddress(payload))
    .then(
      (response): TripLegScoreCacheEntry => ({
        entry: { response, hasPickup, hasDropoff },
      }),
    )
    .catch((err: unknown): TripLegScoreCacheEntry => {
      const error: TripLegConfidenceErrorPayload =
        err && typeof err === 'object'
          ? (err as TripLegConfidenceErrorPayload)
          : { message: String(err) };
      // Every /score/trip-leg error blocks (low confidence is no longer raised
      // here — it now comes from /validate as LOW_CONFIDENCE_ADDRESS). Cache
      // deterministic backend errors (they carry an errorCode), but evict
      // transient/network failures so the next run can retry them.
      if (!error.errorCode) {
        tripLegScoreCache.delete(key);
      }
      return { error };
    });
  tripLegScoreCache.set(key, promise);
  return promise;
}

function hasAddressContent(address: unknown): boolean {
  const line1 = (address as { addressLine1?: unknown } | null | undefined)
    ?.addressLine1;
  return typeof line1 === 'string' && line1.trim().length > 0;
}

/**
 * Single, sequential validation pass shared by every field edit:
 *  1. score every trip leg via /score/trip-leg first (deduped/cached per address);
 *  2. if any leg returns an error, skip /validate entirely — calling it without
 *     the confidence data only produces spurious address errors;
 *  3. otherwise merge the confidence data into the payload and call /validate.
 *
 * Both calls resolve before this returns, so the caller applies a single
 * aggregated result in one transition (no flickering between intermediate states).
 */
export async function runBundledValidation(
  input: BundledValidationInput,
  signal?: AbortSignal,
): Promise<BundledValidationOutput> {
  const { reservation, addressValidationEnabled, isClientPortal } = input;
  if (reservation == null) {
    throw new Error('runBundledValidation: missing reservation payload');
  }

  // Address validation disabled → legacy /validate-only behavior.
  if (!addressValidationEnabled) {
    const validations = await firstValueFrom(
      reservationService.validateReservation(reservation, signal),
    );
    return {
      validations,
      confidenceByTripIndex: {},
      errorsByTripIndex: {},
    };
  }

  const tripLegs = ((reservation as { tripLegs?: unknown[] }).tripLegs ??
    []) as Array<Record<string, unknown>>;
  const confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry> =
    {};
  const errorsByTripIndex: Record<number, TripLegConfidenceErrorPayload> = {};

  // 1. Score every leg first (shared cache dedupes identical addresses).
  await Promise.all(
    tripLegs.map(async (leg, index) => {
      const hasPickup = hasAddressContent(leg?.pickUpAddress);
      const hasDropoff = hasAddressContent(leg?.dropOffAddress);
      if (!hasPickup && !hasDropoff) return;

      const payload = tripLegUtils.getTripLegConfidencePayload(leg);
      const result = await scoreTripLeg(payload, hasPickup, hasDropoff);
      if (result.entry) {
        confidenceByTripIndex[index] = result.entry;
      }
      if (result.error) {
        // Stamp the target form field once, here — the only place with the leg
        // (addresses + metadata) and its index. Both the red field error (hook)
        // and the yellow warning (field component) read `formFieldPath`.
        errorsByTripIndex[index] = {
          ...result.error,
          formFieldPath: resolveConfidenceErrorFieldPath(
            index,
            leg,
            result.error,
          ),
        };
      }
    }),
  );

  // 2. Blocking trip-leg error → skip /validate (it would surface false errors).
  if (hasBlockingTripLegConfidenceError(errorsByTripIndex, isClientPortal)) {
    return {
      validations: EMPTY_VALIDATIONS,
      confidenceByTripIndex,
      errorsByTripIndex,
    };
  }

  // 3. Merge confidence and run /validate.
  const merged = mergeTripLegConfidenceResponsesIntoReservationPayload(
    reservation,
    confidenceByTripIndex,
  );
  let validations: ReservationValidations = EMPTY_VALIDATIONS;
  try {
    const result = await firstValueFrom(
      reservationService.validateReservation(merged, signal),
    );
    validations = {
      ...result,
      errors: filterAddressErrorsForOneSidedTripLegs(
        filterConfidenceErrorsForUnsetPayload(result.errors, merged),
        merged,
      ),
    };
  } catch {
    // /validate failed (e.g. backend address-lookup error). Don't lose the score
    // results — return them with no validate errors so the trip-leg warnings/
    // errors still render and Next gating stays consistent. (If the parent
    // cancelled this run, XState ignores this resolution anyway.)
  }

  return { validations, confidenceByTripIndex, errorsByTripIndex };
}
